import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  User,
  Briefcase,
  ChevronDown,
  ArrowRight,
  Headphones,
  X,
  Phone,
  Mail,
  PhoneCall,
  ArrowLeft,
  Smartphone,
} from "lucide-react";

import ClarityScript from "../../components/ClarityScript/ClarityScript";
import VQrCode from "../../components/VQrCode/VQrCode";
import type { PromoTagVariant } from "../../types/homeLanding";
import type { PromoItem } from "../homeViewTypes";
import fincombankLogo from "../../assets/images/logo/fincombank-logo.png";
import {
  buildDefaultLoginButtons,
  buildDefaultPromoSlides,
} from "./homeDefaultContent";
import classes from "./Home.module.scss";
import HomeSeoHead from "./HomeSeoHead";
import { mapCmsPromoToItem } from "./mapCmsPromoToItem";
import {
  MOBILE_APP,
  getAppDownloadUrl,
  getPersonalQrLink,
} from "../../config/mobileApp";

const tagClass = (v: PromoTagVariant) => {
  switch (v) {
    case "teal":
      return classes.promoTagTeal;
    case "slate":
      return classes.promoTagSlate;
    case "orange":
      return classes.promoTagOrange;
    case "pink":
      return classes.promoTagPink;
    default:
      return classes.promoTagTeal;
  }
};

const BUSINESS_LOGIN_ACTIVE_UNTIL = new Date(2026, 7, 12, 23, 59, 59); // 12 august 2026, local time

function formatTimeLeft(msLeft: number) {
  const safe = Math.max(0, msLeft);
  const totalMinutes = Math.floor(safe / (1000 * 60));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes - days * 60 * 24) / 60);
  const minutes = totalMinutes - days * 60 * 24 - hours * 60;

  if (days > 0) return `${days}z ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

const Home: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [currentPromo, setCurrentPromo] = useState(0);
  const [isHoveringPromo, setIsHoveringPromo] = useState(false);
  const [businessTimeLeft, setBusinessTimeLeft] = useState<string | null>(null);

  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const skipNextPromoClick = useRef(false);
  const currentLang = (i18n.language || "ro").split("-")[0];
  const urlLang = currentLang === "en" ? "ro" : currentLang;

  const defaultLoginButtons = useMemo(() => buildDefaultLoginButtons(t), [t]);
  const defaultPromoSlides = useMemo(
    () => buildDefaultPromoSlides(t, urlLang),
    [t, urlLang],
  );

  const loginButtons = defaultLoginButtons;
  const promoSlides = defaultPromoSlides;

  const promos: PromoItem[] = useMemo(
    () => promoSlides.map((s) => mapCmsPromoToItem(s, classes)),
    [promoSlides],
  );

  const promoCount = promos.length;

  useEffect(() => {
    setCurrentPromo((i) => (promoCount > 0 && i >= promoCount ? 0 : i));
  }, [promoCount]);

  useEffect(() => {
    if (isHoveringPromo) return undefined;
    const interval = window.setInterval(() => {
      setCurrentPromo((prev) => (prev + 1) % promoCount);
    }, 4500);
    return () => window.clearInterval(interval);
  }, [isHoveringPromo, promoCount]);

  useEffect(() => {
    const tick = () => {
      const left = BUSINESS_LOGIN_ACTIVE_UNTIL.getTime() - Date.now();
      setBusinessTimeLeft(left > 0 ? formatTimeLeft(left) : null);
    };

    tick();
    const id = window.setInterval(tick, 30_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!showSupport) return undefined;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [showSupport]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
    setIsHoveringPromo(true);
    skipNextPromoClick.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    setIsHoveringPromo(false);
    const start = touchStartX.current;
    const end = touchEndX.current;
    touchStartX.current = null;
    touchEndX.current = null;
    if (start == null || end == null) return;

    const distance = start - end;
    if (Math.abs(distance) > 50) {
      skipNextPromoClick.current = true;
      if (distance > 0) {
        setCurrentPromo((prev) => (prev + 1) % promoCount);
      } else {
        setCurrentPromo((prev) => (prev === 0 ? promoCount - 1 : prev - 1));
      }
      window.setTimeout(() => {
        skipNextPromoClick.current = false;
      }, 100);
    }
  };

  const handlePromoClick = () => {
    if (skipNextPromoClick.current) return;
    const url = promos[currentPromo]?.url;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const changeLang = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsLangMenuOpen(false);
  };

  return (
    <section className={`${classes.outer} animate-fade-in-up`}>
      <HomeSeoHead lang={currentLang} />
      <ClarityScript />

      <div className={classes.decor} aria-hidden />

      <div className={`${classes.card} animate-fade-in-up`}>
        <div
          className={`${classes.main} ${showSupport ? classes.mainHidden : ""}`}
        >
          <header className={classes.header}>
            <div
              className={classes.logoWrap}
              onClick={() => window.location.reload()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && window.location.reload()}
              aria-label="FinComPay Home"
            >
              <img
                src={fincombankLogo}
                alt="FinComBank"
                className={classes.logoImg}
              />
            </div>

            <nav
              className={classes.langWrap}
              aria-label={t("aria_lang_selector")}
            >
              <button
                type="button"
                className={classes.langBtn}
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                aria-label={t("aria_lang_selector")}
                aria-expanded={isLangMenuOpen}
                aria-haspopup="true"
              >
                <span>{currentLang.toUpperCase()}</span>
                <ChevronDown
                  size={16}
                  style={{
                    transform: isLangMenuOpen ? "rotate(180deg)" : "none",
                  }}
                />
              </button>
              {isLangMenuOpen && (
                <div className={classes.langMenu}>
                  {["ro", "ru", "en"].map((l) => (
                    <button
                      key={l}
                      type="button"
                      className={classes.langOption}
                      onClick={() => changeLang(l)}
                    >
                      {l.toUpperCase()}
                      {currentLang === l && (
                        <span className={classes.langCheck}>✓</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </nav>
          </header>

          <div className={classes.contentGrid}>
            <div className={classes.colPrimary}>
              <div
                className={`${classes.welcome} animate-fade-in-up delay-100`}
              >
                <h1 className={classes.welcomeTitle}>{t("welcome")}</h1>
                <p className={classes.welcomeSub}>{t("instruction")}</p>
              </div>

              <div
                className={`${classes.buttons} animate-fade-in-up delay-200`}
              >
                {loginButtons.map((btn) => {
                  const isPersonal = btn.kind === "personal";
                  const isFeatured = Boolean(btn.featured) && !isPersonal;
                  const showBusinessTimer =
                    btn.kind === "business" && businessTimeLeft != null;
                  const btnClass = [
                    classes.btn,
                    isPersonal ? classes.btnPersonal : classes.btnBusiness,
                    isFeatured ? classes.btnBusinessFeatured : "",
                  ]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <a
                      key={btn.kind}
                      href={btn.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={btnClass}
                    >
                      {isFeatured ? (
                        <div
                          className={`${classes.btnShine} animate-shine`}
                          aria-hidden
                        />
                      ) : null}
                      <div className={classes.btnContent}>
                        <div className={classes.btnIconWrap}>
                          {btn.iconKey === "user" ? (
                            <User size={20} />
                          ) : (
                            <Briefcase size={20} />
                          )}
                        </div>
                        <div>
                          <span className={classes.btnTitle}>{btn.title}</span>
                          <span className={classes.btnDesc}>
                            {btn.description}
                          </span>
                          {showBusinessTimer ? (
                            <span className={classes.btnTimer}>
                              {t("business_timer_active_until", {
                                timeLeft: businessTimeLeft,
                              })}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className={classes.btnArrow}>
                        <ArrowRight size={20} aria-hidden />
                      </div>
                    </a>
                  );
                })}
              </div>

              <section
                className={`${classes.promoSection} animate-fade-in-up delay-300`}
                aria-label={t("aria_promo_banner")}
              >
                <article
                  className={classes.promoBanner}
                  onMouseEnter={() => setIsHoveringPromo(true)}
                  onMouseLeave={() => setIsHoveringPromo(false)}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onClick={handlePromoClick}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handlePromoClick();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-live="polite"
                >
                  {promos.map((promo, index) => (
                    <div
                      key={promo.id}
                      className={`${classes.promoSlide} ${promo.gradientClass} ${
                        index === currentPromo
                          ? classes.promoSlideActive
                          : classes.promoSlideInactive
                      }`}
                    >
                      <div className={classes.promoIconWrap}>{promo.icon}</div>
                      <div className={classes.promoBody}>
                        <div className={classes.promoTitleRow}>
                          {promo.tags.map((tag, tagIndex) => (
                            <span
                              key={tagIndex}
                              className={`${classes.promoTag} ${tagClass(
                                tag.variant,
                              )}`}
                            >
                              {tag.text}
                            </span>
                          ))}
                          <h3 className={classes.promoHeading}>
                            {promo.title}
                          </h3>
                        </div>
                        <p className={classes.promoDesc}>{promo.desc}</p>
                      </div>
                      <div className={classes.promoArrowHint}>
                        <ArrowRight size={20} />
                      </div>
                    </div>
                  ))}

                  <div className={classes.promoDots}>
                    {promos.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        className={`${classes.promoDot} ${
                          index === currentPromo ? classes.promoDotActive : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentPromo(index);
                        }}
                        aria-label={`Promo ${index + 1}`}
                      />
                    ))}
                  </div>
                </article>
              </section>
            </div>

            <div className={classes.colSecondary}>
              <div
                className={`${classes.dividerSection} animate-fade-in-up delay-300`}
              >
                <div className={classes.dividerLine}>
                  <span className={classes.dividerText}>{t("divider")}</span>
                </div>
                <div className={classes.qrSection}>
                  <p className={classes.qrTitle}>{t("app_title")}</p>

                  <div className={classes.qrRow}>
                    <div className={classes.qrItem}>
                      <div className={classes.qrBox}>
                        <div className={classes.qrImg}>
                          <VQrCode
                            link={getPersonalQrLink()}
                            size={112}
                            color="#009CA6"
                            bgColor="#ffffff"
                          />
                        </div>
                      </div>
                      <span className={classes.qrLabel}>
                        {t("qr_personal")}
                      </span>
                    </div>
                    <div className={classes.qrItem}>
                      <div className={classes.qrBox}>
                        <div className={classes.qrImg}>
                          <VQrCode
                            link={MOBILE_APP.businessQr}
                            size={112}
                            color="#009CA6"
                            bgColor="#ffffff"
                          />
                        </div>
                      </div>
                      <span className={classes.qrLabel}>
                        {t("qr_business")}
                      </span>
                    </div>
                  </div>
                  <div className={classes.qrMobile}>
                    <a
                      href={getAppDownloadUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.qrMobileBtnPersonal}
                    >
                      <div className={classes.qrMobileBtnContent}>
                        <div className={classes.qrMobileIconWrap}>
                          <Smartphone size={20} />
                        </div>
                        <div className={classes.qrMobileText}>
                          <span className={classes.qrMobileLabel}>
                            {t("qr_personal")}
                          </span>
                          <span className={classes.qrMobileAction}>
                            {t("download_app")}
                          </span>
                        </div>
                      </div>
                      <ArrowRight size={20} className={classes.qrMobileArrow} />
                    </a>
                    <a
                      href={MOBILE_APP.businessQr}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.qrMobileBtnPersonal}
                    >
                      <div className={classes.qrMobileBtnContent}>
                        <div className={classes.qrMobileIconWrap}>
                          <Briefcase size={20} />
                        </div>
                        <div className={classes.qrMobileText}>
                          <span className={classes.qrMobileLabel}>
                            {t("qr_business")}
                          </span>
                          <span className={classes.qrMobileAction}>
                            {t("download_app")}
                          </span>
                        </div>
                      </div>
                      <ArrowRight size={20} className={classes.qrMobileArrow} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <footer className={`${classes.footer} animate-fade-in-up delay-300`}>
            <button
              type="button"
              className={classes.supportBtn}
              onClick={() => setShowSupport(true)}
            >
              <span className={classes.supportBtnIcon}>
                <Headphones size={14} />
              </span>
              <span>{t("support")}</span>
            </button>
            <span className={classes.footerCopy}>
              © {new Date().getFullYear()} FinComBank S.A.
            </span>
          </footer>
        </div>

        <div
          className={`${classes.modal} ${
            showSupport ? classes.modalOpen : classes.modalClosed
          }`}
          role="dialog"
          aria-label={t("support_page_title")}
        >
          <div className={classes.modalHeader}>
            <div className={classes.modalHeaderLeft}>
              <div className={classes.modalIconWrap}>
                <Headphones size={20} />
              </div>
              <div>
                <h2 className={classes.modalTitle}>
                  {t("support_page_title")}
                </h2>
                <p className={classes.modalSubtitle}>
                  {t("support_page_subtitle")}
                </p>
              </div>
            </div>
            <button
              type="button"
              className={classes.modalClose}
              onClick={() => setShowSupport(false)}
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          <div className={classes.modalBody}>
            <div className={classes.supportGrid}>
              <div className={classes.supportBlock}>
                <h3 className={classes.supportBlockTitle}>
                  {t("contact_center_title")}
                </h3>
                <div className={classes.supportCard}>
                  <div className={classes.supportRow}>
                    <div className={classes.supportRowIcon}>
                      <Phone size={16} />
                    </div>
                    <div>
                      <p className={classes.supportRowLabel}>
                        {t("phone_label")}
                      </p>
                      <div className={classes.supportRowValue}>
                        <a href="tel:+37322269999">+373 22-26-99-99</a>
                      </div>
                    </div>
                  </div>
                  <div className={classes.supportDivider} />
                  <div className={classes.supportRow}>
                    <div className={classes.supportRowIcon}>
                      <Mail size={16} />
                    </div>
                    <div>
                      <p className={classes.supportRowLabel}>
                        {t("email_label")}
                      </p>
                      <a
                        href="mailto:callcenter@fincombank.com"
                        className={classes.supportRowValue}
                      >
                        callcenter@fincombank.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className={classes.supportBlock}>
                <h3 className={classes.supportBlockTitle}>
                  {t("business_support_title")}
                </h3>
                <div className={classes.supportCard}>
                  <div className={classes.supportRow}>
                    <div className={classes.supportRowIcon}>
                      <PhoneCall size={16} />
                    </div>
                    <div>
                      <p className={classes.supportRowLabel}>
                        {t("phone_label")}
                      </p>
                      <div className={classes.supportRowValueMulti}>
                        <a href="tel:+37322269884">+373 22 26-98-84</a>
                        <a href="tel:+37322269864">+373 22 26-98-64</a>
                        <a href="tel:+37322269931">+373 22 26-99-31</a>
                      </div>
                    </div>
                  </div>
                  <div className={classes.supportDivider} />
                  <div className={classes.supportRow}>
                    <div className={classes.supportRowIcon}>
                      <Mail size={16} />
                    </div>
                    <div>
                      <p className={classes.supportRowLabel}>
                        {t("email_label_business")}
                      </p>
                      <a
                        href="mailto:support@fincombank.com"
                        className={classes.supportRowValue}
                      >
                        support@fincombank.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={classes.modalFooter}>
            <button
              type="button"
              className={classes.backBtn}
              onClick={() => setShowSupport(false)}
            >
              <ArrowLeft size={16} />
              <span>{t("back_btn")}</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Home;
